// src/components/support/forms/ShiftCenter.js
import React, { useState, useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import moment from "moment";
import { useRequestProcessor } from "../../../requestProcessor";
import SpinnerLoading from "../../common/SpinnerLoading";
import {
    fetchCenterProgramTimeSlots,
    fetchCentersShift,
    fetchProgram,
    handleShiftCenter,
} from "../../../queryfunctions/queryfunctions";
import FootprintDatePicker, {
    DEFAULT_FORMAT,
} from "../../common/FootprintDatePicker";
import { useAtom } from "jotai";
import { childList, shiftCenterFormState } from "../../../atomstore/store";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../common/CustomSelect";

const ShiftCenter = () => {
    const navigate = useNavigate();
    const { useMutationWrapper, useQueryWrapper } = useRequestProcessor();
    const isRestrictionActive = moment().isSameOrAfter("2026-04-01", "day");
    // ✅ Use atom for form state persistence
    const [formState, setFormState] = useAtom(shiftCenterFormState);

    const [attritionDate, setAttritionDate] = useState(formState?.attritionDate || null);
    const [joiningDate, setJoiningDate] = useState(formState?.joiningDate || null);
    const [selectedCenter, setSelectedCenter] = useState(formState?.selectedCenter || null);
    const [selectedProgram, setSelectedProgram] = useState(formState?.selectedProgram || null);
    const [centerPrograms, setCenterPrograms] = useState([]);
    const [children] = useAtom(childList);
    const firstNonPausedIndex = children.findIndex(c => c?.pause_state !== 1);
    const defaultChildIndex = firstNonPausedIndex >= 0 ? firstNonPausedIndex : 0;
    const [selectedChild, setSelectedChild] = useState(formState?.selectedChild ?? defaultChildIndex);
    const [programs, setProgram] = useState()
    const [error, setError] = useState();
    const [hasInformedCenter, setHasInformedCenter] = useState(
        formState?.hasInformedCenter || false
    );


    useEffect(() => {
        if (formState) {

            if (formState.attritionDate) {
                setAttritionDate(formState.attritionDate);
            }
            if (formState.joiningDate) {
                setJoiningDate(formState.joiningDate);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formState?.attritionDate, formState?.joiningDate]);


    useEffect(() => {
        setFormState({
            attritionDate,
            joiningDate,
            selectedCenter,
            selectedProgram,
            selectedChild,
            hasInformedCenter
        });
    }, [attritionDate, joiningDate, selectedCenter, selectedProgram, selectedChild, setFormState, hasInformedCenter]);

    const {
        data: fetched_program_slots,
        isLoading,
    } = useQueryWrapper(
        ["program_slots", children[selectedChild]?.id],
        () => fetchCenterProgramTimeSlots(children[selectedChild]),
        {
            enabled: !!children[selectedChild]?.id,
            onSuccess: (data) => {
                // Example: set first program by default
                if (data && data.length > 0) {
                    setProgram(data);
                }
            },
        }
    );



    // ✅ Fetch list of centers
    const { data: centers, isLoading: isCentersLoading } = useQueryWrapper(
        ["CentersShift", selectedChild?.id],
        () => fetchCentersShift(children[selectedChild]),
        {
            onSuccess: (data) => {
                const prog = data?.find((obj) => {
                    const centerData = JSON.parse(obj.value);
                    return (
                        centerData.Center_Id ===
                        children[selectedChild]?.center_id?.toString()
                    );
                });
                if (prog) {
                    const programsAvailable = JSON.parse(prog.value).Programs_Available;
                    setCenterPrograms(programsAvailable.split(","));
                }
            },
        }
    );

    console.log("selected center", selectedCenter)

    // ✅ Fetch available programs
    // const { data: programs, isLoading: isProgramsLoading } =
    //     useQueryWrapper(["Programs"], fetchProgram);

    // ✅ Mutation for submit
    const shiftCenter = useMutationWrapper(
        ["shiftCenter", "shift" + selectedChild?.id],
        () =>
            handleShiftCenter(
                joiningDate,
                attritionDate,
                selectedCenter,
                selectedProgram,
                children[selectedChild]
            ),
        {
            onSuccess: (data) => {
                console.log("[Shift Center] Success:", data);
                // Clear saved form state after successful submission
                setFormState({
                    attritionDate: null,
                    joiningDate: null,
                    selectedCenter: null,
                    selectedProgram: null,
                    selectedChild: 0,
                    hasInformedCenter: false,
                });
                // Reset local state
                setAttritionDate(null);
                setJoiningDate(null);
                setSelectedCenter(null);
                setSelectedProgram(null);
                setSelectedChild(0);
                setHasInformedCenter(false);
                // onBack()
            },
        }
    );

    // ✅ Validation
    const validate = () => {
        if (!isRestrictionActive && !attritionDate) {
            setError("Please select last day at current center");
            return false;
        }
        if (!joiningDate) {
            setError("Please select first day at new center");
            return false;
        }
        if (!selectedCenter) {
            setError("Please select new center");
            return false;
        }
        if (!selectedProgram) {
            setError("Please select program");
            return false;
        }

        const diff = moment(joiningDate, DEFAULT_FORMAT).diff(
            moment(attritionDate, DEFAULT_FORMAT),
            "days"
        );
        if (diff <= 0) {
            setError("Joining date must be after last day at current center");
            return false;
        }
        if (diff > 8) {
            setError("Joining date must be within 8 days of leaving date");
            return false;
        }
        if (isRestrictionActive && !hasInformedCenter) {
            setError(
                "Please confirm that you have visited the current center and informed the center head"
            );
            return false;
        }

        return true;
    };

    const handleHoliday = (date, setter) => {
        if (date?.isHoliday) {
            setError("The selected date cannot be a holiday");
            setter(undefined);
        } else {
            setError(undefined);
            setter(moment(date).format(DEFAULT_FORMAT));
        }
    };

    // When user selects first day at new center, automatically set
    // last day at current center as one day before.
    const handleJoiningDateChange = (date) => {
        if (date?.isHoliday) {
            setError("The selected date cannot be a holiday");
            setJoiningDate(undefined);
            setAttritionDate(undefined);
        } else {
            setError(undefined);
            const joining = moment(date).format(DEFAULT_FORMAT);
            const lastDay = moment(date).subtract(1, "days").format(DEFAULT_FORMAT);
            setJoiningDate(joining);
            setAttritionDate(lastDay);
        }
    };

    // ✅ Submit handler
    const submit = () => {
        setError(undefined);
        if (validate()) shiftCenter.mutate();
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {(isCentersLoading || isLoading || shiftCenter.isLoading) && (
                <SpinnerLoading />
            )}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Shift Center</h2>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
                {/* Child Selector */}
                {children.filter(c => c?.pause_state !== 1).length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            value={selectedChild}
                            onChange={(val) => setSelectedChild(Number(val))}
                            options={children
                                .map((child, idx) => ({ value: idx, label: `${child?.name} ${child?.last_name}` }))
                                .filter(opt => children[opt.value]?.pause_state !== 1)
                            }
                            placeholder="Select Child"
                        />
                    </div>
                )}

                {/* Attrition Date */}
                {!isRestrictionActive && <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Day at Current Center
                    </label>
                    <FootprintDatePicker
                        disableWeekends
                        defaultDate={
                            attritionDate
                                ? moment(attritionDate, DEFAULT_FORMAT).toDate()
                                : null
                        }
                        onChange={(date) => handleHoliday(date, setAttritionDate)}
                        placeholder="Select Date"
                        minimumDate={moment().startOf("day")}
                        onError={(err) => setError(err)}
                    />
                </div>
                }
                {/* Joining Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Day at New Center
                    </label>
                    <FootprintDatePicker
                        disableWeekends
                        defaultDate={
                            joiningDate ? moment(joiningDate, DEFAULT_FORMAT).toDate() : null
                        }
                        onChange={isRestrictionActive ? handleJoiningDateChange : (date) => handleHoliday(date, setJoiningDate)}
                        placeholder="Select Date"
                        minimumDate={moment().add(1, "days").startOf("day")}
                        onError={(err) => setError(err)}
                        {...(!isRestrictionActive && {
                            disableCalendarOpen: !attritionDate,
                            minimumDate: attritionDate
                                ? moment(attritionDate, DEFAULT_FORMAT).add(1, "days")
                                : moment(),
                            maximumDate: attritionDate
                                ? moment(attritionDate, DEFAULT_FORMAT).add(8, "days")
                                : undefined,
                        })}
                    />
                </div>

                {/* Center */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Center
                    </label>
                    <CustomSelect
                        value={selectedCenter ? JSON.stringify(selectedCenter) : ""}
                        onChange={(val) => {
                            const center = val === "" ? "" : JSON?.parse(val);
                            setSelectedCenter(center);
                            // Clear selected program when center changes
                            if (center !== selectedCenter) {
                                setSelectedProgram(undefined);
                            }
                            setError(undefined);
                        }}
                        options={[
                            { value: "", label: "Select center" },
                            ...(centers?.map((item, index) => ({
                                value: item.value,
                                label: item.label,
                            })) || []),
                        ]}
                        placeholder="Select center"
                    />
                </div>

                {/* Program */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Program
                    </label>
                    <CustomSelect
                        value={selectedProgram?.time_slot_program_name || ""}
                        onChange={(value) => {
                            setError(undefined);

                            // value === opt.value === time_slot_program_name
                            const selectedObj = programs?.find(
                                (p) => p.time_slot_program_name === value
                            );

                            setSelectedProgram(selectedObj);
                        }}
                        options={programs?.map((p) => ({ value: p?.time_slot_program_name, label: p?.time_slot_display_name }))}
                        placeholder="Select program"
                    />
                </div>

                {/* Fee Card Link */}
                {selectedCenter?.Center_Feecard && (
                    <div className="text-center">
                        <a
                            href={selectedCenter.Center_Feecard}
                            target="_blank"
                            rel="noreferrer"
                            className="text-cyan-600 font-medium underline"
                        >
                            View Fee Details
                        </a>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="text-red-500 text-sm text-center">{error}</div>
                )}

                {/* Submit */}
                {isRestrictionActive && (
                    <div className="flex items-start space-x-3">
                        <input
                            type="checkbox"
                            id="centerDeclaration"
                            checked={hasInformedCenter}
                            onChange={(e) => {
                                setHasInformedCenter(e.target.checked);
                                setError(undefined);
                            }}
                            className="mt-1 h-4 w-4 text-cyan-600 border-gray-300 rounded"
                        />
                        <label
                            htmlFor="centerDeclaration"
                            className="text-sm text-gray-700 leading-relaxed"
                        >
                            I confirm that I have visited the current center and informed the
                            Center Head before applying for this shift request.
                        </label>
                    </div>)}
                <button
                    onClick={submit}
                    disabled={shiftCenter.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition"
                >
                    {shiftCenter.isLoading
                        ? "Submitting..."
                        : "Submit Transfer Request"}
                </button>
            </div>
        </div>
    );
};

export default ShiftCenter;
